import { useTranslation } from 'react-i18next';
import type { tConnectionFailedAdvisorCtaKind } from 'src/components/WhatsAppConnect/ConnectionFailedError/ConnectionFailedError.types';
import { getEmbeddedSignupAttemptId } from 'src/lib/facebook/embeddedSignupTelemetry';
import {
  WIZARD_BODY_CLASS_NAME,
  WIZARD_CAPTION_CLASS_NAME,
  WIZARD_SCREEN_SUBTITLE_CLASS_NAME,
  WIZARD_SCREEN_TITLE_CLASS_NAME,
  WIZARD_TRANSLATION_NAMESPACE,
} from './WhatsAppConnectionWizard.constants';
import type { tWizardActivationFailure } from './WhatsAppConnectionWizard.types';
import { WhatsAppConnectionWizardAdvisorRow } from './WhatsAppConnectionWizardExternalLink.component';

export type tWhatsAppConnectionWizardActivationFailedProps = {
  activationFailure: tWizardActivationFailure;
  onAdvisorCtaClick: (kind: tConnectionFailedAdvisorCtaKind) => void;
};

/**
 * The main pane when Meta did not activate the number. WBS keeps asking Meta
 * for a day after the connection, so the screen says the result can still
 * change. When retrying no longer fixes it, the body only says why and the
 * footer leads with the advisor; otherwise the body ends with the advisor row.
 *
 * @param {tWhatsAppConnectionWizardActivationFailedProps} props - The
 * resolved failure and the advisor click report.
 * @returns {JSX.Element} The activation failure screen.
 */
export default function WhatsAppConnectionWizardActivationFailed(
  props: tWhatsAppConnectionWizardActivationFailedProps,
) {
  const { activationFailure, onAdvisorCtaClick } = props;
  const { reasonKey, isRepeat, footer } = activationFailure;
  const { t } = useTranslation(WIZARD_TRANSLATION_NAMESPACE);
  // Kept in memory only, so a failure restored after a reload has none, and
  // no code is shown rather than one made up.
  const attemptId =
    reasonKey === 'unknown' ? getEmbeddedSignupAttemptId() : null;

  return (
    <div className="flex flex-col gap-[var(--ls-spacing-6)]">
      <div className="flex flex-col gap-[var(--ls-spacing-2)]">
        <h2 className={WIZARD_SCREEN_TITLE_CLASS_NAME}>
          {t(`wizard.activationFailed.${reasonKey}.title`)}
        </h2>
        <p className={WIZARD_SCREEN_SUBTITLE_CLASS_NAME}>
          {t(`wizard.activationFailed.${reasonKey}.subtitle`)}
        </p>
        <p className={WIZARD_BODY_CLASS_NAME}>
          {t('wizard.activationFailed.reprobeLine')}
        </p>
      </div>
      <div className="flex flex-col gap-[var(--ls-spacing-2)]">
        <p className={WIZARD_BODY_CLASS_NAME}>
          {t(
            isRepeat
              ? 'wizard.activationFailed.repeatBody'
              : `wizard.activationFailed.${reasonKey}.body`,
          )}
        </p>
        {attemptId && (
          <p className={WIZARD_CAPTION_CLASS_NAME}>
            {t('wizard.closedBeforeFinishing.attemptCodeLine', { attemptId })}
          </p>
        )}
      </div>
      {footer.primary !== 'advisor' && (
        <WhatsAppConnectionWizardAdvisorRow
          onAdvisorCtaClick={onAdvisorCtaClick}
        />
      )}
    </div>
  );
}
